import React, { useMemo, useState } from "react";
import { Button, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useListAndSearchAllAccounts } from "@rentablo/api/apiv1";
import PlusIcon from "@icons/myicons/plus.svg?react";
import { AccountEditCard } from "@features/accounts/edit/components/AccountEditCard";
import { deriveContraAccounts } from "@features/accounts/edit/components/AdditionalContraAccounts/deriveContraAccounts";
import { Option } from "@features/accounts/edit/components/AdditionalContraAccounts/deriveContraAccounts";
import { SelectAdditionalContraAccountsModal } from "@features/accounts/edit/components/AdditionalContraAccounts/SelectAdditionalContraAccountsModal";
import { useReferenceAccountActions } from "../hooks/useReferenceAccountActions";
import { PrimaryAccountSection } from "./PrimaryAccountSection";
import { AdditionalAccountsSection } from "./AdditionalAccountsSection";

interface Props {
  depotId: number;
}

export const ReferenceAccountsCard = ({ depotId }: Props) => {
  const { t } = useTranslation();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data: accountList } = useListAndSearchAllAccounts();
  const accounts = useMemo(() => accountList?.accounts ?? [], [accountList]);
  const depot = accounts.find((a) => a.id === depotId);

  const {
    handleChangePrimary,
    handleSetPrimary,
    handleRemove,
    handleAddAdditional,
    isMutating,
    pendingAccountId,
  } = useReferenceAccountActions(depot);

  const otherDepots = useMemo(
    () => accounts.filter((a) => a.type === "01_depot" && a.id !== depotId),
    [accounts, depotId]
  );

  // Accounts already linked to another depot — either as its primary contra
  // account or as one of its additional ones — must not be offered here.
  const takenIds = useMemo(
    () =>
      new Set(
        otherDepots
          .flatMap((a) => [
            a.contraAccountId,
            ...(a.additionalContraAccountIds ?? []),
          ])
          .filter((id): id is number => id !== undefined)
      ),
    [otherDepots]
  );

  const linkedIds = useMemo(() => {
    if (!depot) return new Set<number>();
    return new Set(
      [
        depot.contraAccountId,
        ...(depot.additionalContraAccountIds ?? []),
      ].filter((id): id is number => id !== undefined)
    );
  }, [depot]);

  const primaryCandidates = useMemo(() => {
    if (!depot) return [];
    return accounts
      .filter(
        (a) =>
          a.type === "02_cash" &&
          Boolean(a.bankConnectionType) &&
          !linkedIds.has(a.id) &&
          !takenIds.has(a.id)
      )
      .sort((a, b) => {
        const aOwn = a.bankConnectionId === depot.bankConnectionId ? 0 : 1;
        const bOwn = b.bankConnectionId === depot.bankConnectionId ? 0 : 1;
        return aOwn - bOwn;
      });
  }, [accounts, depot, linkedIds, takenIds]);

  const addCandidates = useMemo(() => {
    if (!depot) return [];
    return accounts.filter(
      (a) =>
        a.type === "02_cash" &&
        Boolean(a.bankConnectionType) &&
        a.bankConnectionId === depot.bankConnectionId &&
        !linkedIds.has(a.id) &&
        !takenIds.has(a.id)
    );
  }, [accounts, depot, linkedIds, takenIds]);

  if (!depot || depot.type !== "01_depot") return null;

  const { primary, additional } = deriveContraAccounts(accounts, depot);

  const addOptions: Option[] = addCandidates.map((a) => ({
    id: a.id,
    accountName: a.name,
    iban: a.iban,
    bankName: a.bankName,
    bankImage: a.iconUrl,
    type: a.typeFromBank,
  }));

  const handleToggle = (id: number) =>
    setSelectedIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
    );

  const handleSubmit = async () => {
    if (!selectedIds.length) {
      setIsAddOpen(false);
      return;
    }
    try {
      await handleAddAdditional(selectedIds);
      setIsAddOpen(false);
      setSelectedIds([]);
    } catch {
      // error shown via snackbar in handleAddAdditional
    }
  };

  return (
    <AccountEditCard
      title={t("referenceAccountPage.accountsCard.title")}
      description={t("referenceAccountPage.accountsCard.description")}
      testId="reference-accounts-card"
    >
      <PrimaryAccountSection
        primary={primary}
        candidates={primaryCandidates}
        isMutating={isMutating}
        canChange={Boolean(depot.bankConnectionType)}
        onChangePrimary={handleChangePrimary}
      />
      {additional.length > 0 && <Divider sx={{ my: 4 }} />}
      <AdditionalAccountsSection
        accounts={additional}
        isMutating={isMutating}
        pendingAccountId={pendingAccountId}
        onSetPrimary={handleSetPrimary}
        onRemove={handleRemove}
      />
      {addOptions.length > 0 && (
        <Button
          variant="text"
          size="small"
          color="primary"
          startIcon={<PlusIcon width={16} height={16} />}
          onClick={() => {
            setSelectedIds([]);
            setIsAddOpen(true);
          }}
          sx={{ mt: 2 }}
          data-testid="add-additional-account-button"
        >
          {t("referenceAccountPage.addTrigger")}
        </Button>
      )}
      <SelectAdditionalContraAccountsModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        options={addOptions}
        selectedIds={selectedIds}
        onToggle={handleToggle}
        onSubmit={handleSubmit}
        isFetching={isMutating}
      />
    </AccountEditCard>
  );
};
